import { Eye, EyeOff } from "lucide-react";
import { usePriceVisibility } from "@/hooks/usePriceVisibility";

// One-tap "Presentation Mode" toggle for logged-in traders.
// When OFF, every price on the catalog / series / card / WhatsApp text is
// replaced with an em-dash so the trader can hand their device to a retail
// customer without exposing wholesale prices.
//
// The trader explicitly asked for the toggle + all associated indicators to
// be DISCREET so a retail customer glancing over their shoulder can't tell
// the device is in a special mode. So the compact pill is icon-only (no
// "HIDE" / "PRESENTING" label) and the on-page banner is removed entirely.
// The single visual cue that remains is the Eye ↔ EyeOff icon swap.

export function PriceVisibilityToggle({ variant = "compact" }) {
  const { visible, toggle } = usePriceVisibility();
  const Icon = visible ? Eye : EyeOff;
  const baseTestId = "price-visibility-toggle";

  if (variant === "inline") {
    return (
      <button
        type="button"
        onClick={toggle}
        data-testid={`${baseTestId}-inline`}
        data-visible={visible ? "true" : "false"}
        className="flex items-center gap-2 px-3 py-2 text-sm text-[#4A4A4A] hover:text-[#D62828] transition-colors"
      >
        <Icon size={16} strokeWidth={1.5} />
        <span>{visible ? "Hide prices" : "Show prices"}</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={toggle}
      data-testid={baseTestId}
      data-visible={visible ? "true" : "false"}
      title={visible ? "Hide prices" : "Show prices"}
      aria-label={visible ? "Hide prices" : "Show prices"}
      className="inline-flex items-center justify-center w-9 h-9 border border-[#E5E0D8] text-[#4A4A4A] hover:border-[#D62828] hover:text-[#D62828] transition-colors"
    >
      <Icon size={14} strokeWidth={1.75} />
    </button>
  );
}

// Retained as an exported no-op so any lingering imports don't blow up.
// The trader asked for zero on-page indicators when Presentation Mode is on,
// so this component intentionally renders nothing.
export function PresentationModeBanner() {
  return null;
}
